/**
 * POS · Corrections — void / refund a SETTLED order. Look it up by id, review
 * its lines, then full-void or line-exact partial-refund. POS-cap server-side.
 * FUNCTIONAL scaffold for the design pass.
 */

import { useCallback, useState } from "react";

import {
  App as AntApp,
  Alert,
  Button,
  Card,
  Descriptions,
  InputNumber,
  Space,
  Table,
  Typography,
} from "antd";

import {
  ApiError,
  api,
  type CorrectionResponse,
  type PosOrder,
  type PosOrderLine,
} from "../../api/client";

const eur = (c: number) => `€${(c / 100).toFixed(2)}`;
const newKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `corr-${Date.now()}`;

export const PosCorrectionsPage = () => {
  const { message } = AntApp.useApp();
  const [orderId, setOrderId] = useState<number | null>(null);
  const [order, setOrder] = useState<PosOrder | null>(null);
  const [picked, setPicked] = useState<number[]>([]);
  const [result, setResult] = useState<CorrectionResponse | null>(null);
  const [busy, setBusy] = useState(false);

  const lookup = useCallback(async () => {
    if (orderId == null) return;
    setResult(null);
    setPicked([]);
    try {
      setOrder(await api.posGetOrder(orderId));
    } catch (e) {
      setOrder(null);
      message.error(e instanceof ApiError ? e.detail : "Order not found");
    }
  }, [orderId, message]);

  const correct = useCallback(
    async (full: boolean) => {
      if (!order) return;
      setBusy(true);
      try {
        const r = await api.posCorrectOrder(order.id, {
          client_idempotency_key: newKey(),
          line_ids: full ? null : picked,
        });
        setResult(r);
        message.success(`${r.correction_type} → ${r.correction_receipt_no}`);
      } catch (e) {
        message.error(e instanceof ApiError ? e.detail : "Correction failed");
      } finally {
        setBusy(false);
      }
    },
    [order, picked, message],
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>POS corrections</Typography.Title>

      <Card title="Find a settled order">
        <Space>
          <InputNumber
            placeholder="Order id"
            min={1}
            value={orderId}
            onChange={setOrderId}
          />
          <Button type="primary" onClick={lookup} disabled={orderId == null}>
            Look up
          </Button>
        </Space>
      </Card>

      {order && (
        <Card
          title={`Order #${order.id} — ${order.status} — ${eur(order.total_cents)}`}
        >
          <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Tender">{order.tender ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Receipt">
              {order.receipt_no ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Total">{eur(order.total_cents)}</Descriptions.Item>
          </Descriptions>

          {order.status !== "settled" ? (
            <Alert
              type="info"
              showIcon
              message="Only a settled order can be corrected."
            />
          ) : (
            <>
              <Table<PosOrderLine>
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={order.lines}
                rowSelection={{
                  selectedRowKeys: picked,
                  onChange: (keys) => setPicked(keys as number[]),
                }}
                columns={[
                  { title: "Line", dataIndex: "id", width: 70 },
                  { title: "Kind", dataIndex: "kind" },
                  { title: "Article", dataIndex: "article_id" },
                  { title: "Qty", dataIndex: "qty" },
                  {
                    title: "Unit",
                    dataIndex: "unit_price_cents",
                    render: (c: number) => eur(c),
                  },
                ]}
              />
              <Space style={{ marginTop: 12 }}>
                <Button danger loading={busy} onClick={() => void correct(true)}>
                  Void whole order
                </Button>
                <Button
                  loading={busy}
                  disabled={picked.length === 0}
                  onClick={() => void correct(false)}
                >
                  Refund selected lines ({picked.length})
                </Button>
              </Space>
            </>
          )}
        </Card>
      )}

      {result && (
        <Alert
          type="success"
          showIcon
          message={`${result.correction_type} → receipt ${result.correction_receipt_no}`}
          description={
            <Space direction="vertical">
              <span>Refunded {eur(result.refunded_total_cents)}</span>
              {result.card_refund && <span>Card refund: {result.card_refund}</span>}
            </Space>
          }
        />
      )}
    </Space>
  );
};
